import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {action, makeAutoObservable, observable} from "mobx";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import type {IContestPlayer} from "data/stores/contest_players/contest_players.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IContestsStore} from "data/stores/contests/contests.store";
import {AxiosError} from "axios";
import {IApiResponse} from "data/services/http";
import {extractErrorMessage} from "data/utils";
import {isNull} from "lodash";
import {AnalyticService} from "data/utils/track_analytics";

interface IProps {
	player: IContestPlayer;
	gameFeedId: number;
}

export interface IPlayerCardController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get squadByPlayer(): ISquad | null | undefined;
	get hasPrediction(): boolean;
	get isPlayerSelected(): boolean;
	get isSelectedPlayerEliminated(): boolean;
	get playerCardClass(): string;
	get summaryStat(): string;
	get isPitcher(): boolean;

	onSelect: () => void;
}

@injectable()
export class PlayerCardController implements IPlayerCardController {
	@observable private _player: IContestPlayer | null = null;
	@observable private _gameFeedId: number = 0;

	get squadByPlayer() {
		if (!this._player) return null;

		return this._squadsStore.getSquadById(this._player.squadId);
	}

	get hasPrediction() {
		return (
			Boolean(this._predictionsStore.prediction?.contestId) &&
			this._player?.playerId === this._predictionsStore.prediction?.playerId
		);
	}

	get isPlayerSelected() {
		return this._predictionsStore.prediction?.playerId === this._player?.playerId;
	}

	get isSelectedPlayerEliminated() {
		return this.isPlayerSelected && !isNull(this._player) && this._player.status !== "active";
	}

	get playerCardClass() {
		if (this.isSelectedPlayerEliminated) {
			return "eliminated";
		}

		if (this.isPlayerSelected && !isNull(this._player)) {
			return "selected";
		}

		return "";
	}

	get summaryStat() {
		if (this._player?.stats?.hitting?.summary) {
			return this._player.stats.hitting.summary;
		}

		if (this._player?.stats?.pitching?.summary) {
			return this._player.stats.pitching.summary;
		}

		return "";
	}

	get isPitcher() {
		return this._player?.position === "pitcher";
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.SquadsStore) public _squadsStore: ISquadsStore,
		@inject(Bindings.PredictionsStore) public _predictionsStore: IPredictionsStore,
		@inject(Bindings.ModalsStore) public _modalStore: IModalsStore,
		@inject(Bindings.PlayersStore) public _playerStore: IPlayersStore,
		@inject(Bindings.ContestsStore) public _contestStore: IContestsStore
	) {
		makeAutoObservable(this);
	}

	onSelect = () => {
		const currentContest = this._contestStore.getContestByFeedId(this._gameFeedId);
		if (!this._player || !currentContest?.id) return null;

		this._predictionsStore.setPrediction({
			contestId: currentContest.id,
			playerId: this._player?.playerId,
		});

		AnalyticService.trackAction({
			"action.name": "Player of the Game: Player Select Click",
			"action.location": "Major League Baseball: Player of the Game: Vote",
			"action.category": "Player of the Game",
			"action.element.text": "Selection",
			"baseball.playerId": String(this._player?.feedId),
			"baseball.gamePk": String(currentContest.gameFeedId),
		});
	};

	@action private onError = (error: AxiosError<IApiResponse>) => {
		const errorMsg = extractErrorMessage(error);

		this._modalStore.showModal(ModalType.ERROR, {
			message: errorMsg,
		});
	};

	// private onConfirmVote = () => {
	// 	void this._predictionsStore
	// 		.savePrediction({
	// 			contestId: this._contestId ?? 0,
	// 			playerId: this._player?.playerId ?? 0,
	// 		})
	// 		.catch((err: AxiosError<IApiResponse>) => this.onError(err))
	// 		.finally(() => this._modalStore.hideModal());
	// };

	init({player, gameFeedId}: IProps) {
		this._player = player;
		this._gameFeedId = gameFeedId;
	}
}
